/*
 * This file adapted from ThreeJS OrbitControls:
 * https://github.com/mrdoob/three.js/blob/e22cb060cc91283d250e704f886528e1be593f45/examples/jsm/controls/OrbitControls.js
 *
 * Licensed under MIT license.
 *
 * The MIT License
 *
 * Copyright © 2022 VRIFY Technology, Inc
 * Copyright © 2010-2022 three.js authors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

import { Vector2 } from 'three'
import { getChangedFov } from '@/lib/site-viewer/utils'
import { degToRad, radToDeg } from 'three/src/math/MathUtils'
import { HostSiteControls } from '@/lib/site-viewer/hostSiteControls'
import { ControlEvent } from '@/lib/site-viewer/siteViewerControls'

class HostSiteControlsWithZoom extends HostSiteControls {
  private _isTouchZoomActive = false
  private _pointers: PointerEvent[] = []
  private _pointerPositions: Record<any, Vector2> = {}
  private _dollyStart: number | null = null

  protected override _onPointerDown = (event: PointerEvent) => {
    this.dispatchEvent({ type: ControlEvent.PointerDown })
    const domElement = this._domElements[0]

    if (event.pointerType !== 'touch' || !domElement) return

    if (this._pointers.length === 0) {
      domElement.setPointerCapture(event.pointerId)

      domElement.addEventListener('pointermove', this._onPointerMove)
      domElement.addEventListener('pointerup', this._onPointerUp)
    }

    this._pointers.push(event)
    this._trackPointer(event)

    if (this._pointers.length === 2) {
      this._handleTouchStartDolly()
      this._isTouchZoomActive = true
    } else {
      this._isTouchZoomActive = false
    }

    if (this._isTouchZoomActive) {
      this.orbitControls?.dispatchEvent({ type: 'start' })
    }
  }

  private _onPointerMove = (event: PointerEvent) => {
    if (event.pointerType !== 'touch') return

    this._trackPointer(event)

    if (this._isTouchZoomActive) {
      this._handleTouchMoveDolly(event)
      this.orbitControls?.update()
    }
  }

  private _onPointerUp = (event: PointerEvent) => {
    this._removePointer(event)

    const domElement = this._domElements[0]
    if (this._pointers.length === 0 && domElement) {
      domElement.releasePointerCapture(event.pointerId)

      domElement.removeEventListener('pointermove', this._onPointerMove)
      domElement.removeEventListener('pointerup', this._onPointerUp)
    }

    this.orbitControls?.dispatchEvent({ type: 'end' })

    this._isTouchZoomActive = false
  }

  protected override _onPointerCancel = (event: PointerEvent) => {
    this._removePointer(event)
  }

  private _getSecondPointerPosition = (event: PointerEvent) => {
    const pointer = event.pointerId === this._pointers[0].pointerId ? this._pointers[1] : this._pointers[0]
    return this._pointerPositions[pointer.pointerId]
  }

  private _handleTouchStartDolly = () => {
    const dx = this._pointers[0].pageX - this._pointers[1].pageX
    const dy = this._pointers[0].pageY - this._pointers[1].pageY
    const distance = Math.sqrt(dx * dx + dy * dy)
    this._dollyStart = distance
  }

  private _handleTouchMoveDolly = (event: PointerEvent) => {
    if (!this.viewer?.camera.isPerspectiveCamera) {
      throw new Error('Expected perspective camera')
    }
    if (!this._dollyStart) {
      throw new Error('Touchstart event must set dolly start distance')
    }

    const position = this._getSecondPointerPosition(event)

    const dx = event.pageX - position.x
    const dy = event.pageY - position.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    const dollyEnd = distance

    const newFov = radToDeg(getChangedFov(degToRad(this.viewer.camera.fov), this._dollyStart / dollyEnd))

    this.updateZoom({ fov: newFov, shouldPublish: true })

    this._dollyStart = dollyEnd
  }

  private _removePointer = (event: PointerEvent) => {
    delete this._pointerPositions[event.pointerId]

    for (let i = 0; i < this._pointers.length; i++) {
      if (this._pointers[i].pointerId == event.pointerId) {
        this._pointers.splice(i, 1)
        return
      }
    }
  }

  private _trackPointer = (event: PointerEvent) => {
    let position = this._pointerPositions[event.pointerId]

    if (position === undefined) {
      this._pointerPositions[event.pointerId] = position = new Vector2()
    }

    position.set(event.pageX, event.pageY)
  }
}

export { HostSiteControlsWithZoom }
